import React, { useEffect, useState } from "react"
// This style file affecting current vehicle scheduler UI so removing this as of now.
// import "./FullCalendar.css";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  type Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Chip,
  Tooltip,
  alpha,
} from "@mui/material"
import FullCalendar from "@fullcalendar/react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useSelector } from "react-redux"
import type { IAppState } from "../../../../store"
import interactionPlugin from "@fullcalendar/interaction"
import resourceTimelinePlugin from "@fullcalendar/resource-timeline"
import { formatGraphQLErrorMessage, ViewMode } from "../../../common/utils"
import { DateTime as d } from "luxon"
import { type ApolloError, useLazyQuery } from "@apollo/client"
import { GET_DRIVER_SCHEDULER } from "../../../../graphql/driverScheduler/getDriverScheduler"
import { useSnackBar } from "../../../common/SnackBarContext/SnackBarContext"
import { SnackBarVariant } from "../../../common/SnackbarWrapper/SnackbarWrapper"
import SearchIcon from "@mui/icons-material/Search"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import TodayIcon from "@mui/icons-material/Today"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import DateRangeIcon from "@mui/icons-material/DateRange"
import ViewDayIcon from "@mui/icons-material/ViewDay"
import { EventDetailsDialog } from "./EventDetailsDialog"
import { makeStyles } from "@mui/styles"
import { REALLOCATE_INTERNAL_DRIVER_JOB } from "../../../../graphql/driverScheduler/jobReallocation"

const useStyles = makeStyles((theme: Theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      transition: "all 0.3s",
      "&:hover": {
        boxShadow: "0 0 0 2px rgba(0, 123, 255, 0.1)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(0, 123, 255, 0.2)",
      },
    },
  },
  calendarContainer: {
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
    },
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    marginRight: 16,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    marginRight: 8,
  },
  dateNavigation: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  navButton: {
    borderRadius: 8,
    padding: "8px 16px",
    fontWeight: 500,
    transition: "all 0.2s ease",
    boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
    },
  },
  currentDateDisplay: {
    fontWeight: 600,
    padding: "0 16px",
    minWidth: 180,
    textAlign: "center",
  },
}))

export default function DriverScheduler() {
  const fullCalendarRef = React.createRef<FullCalendar>()
  const snackbar = useSnackBar()
  const classes = useStyles()
  const userReducer = useSelector((state: IAppState) => state.userReducer)
  const [resources, setResources] = useState<any[]>([])
  const [activeResources, setActiveResources] = useState<any[]>([])
  const [driverEvents, setDriverEvents] = useState<any[]>([])
  const [currentDate, setCurrentDate] = useState(d.now().toUTC())
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.WEEK)
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [selectedEvent, setSelectedEvent] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [getSchedulerData, { data: schedulerData, loading: schedulerLoading }] = useLazyQuery(GET_DRIVER_SCHEDULER, {
    variables: {
      view: viewMode,
      currentDate: currentDate.toISODate(),
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data && data.getDriverUserScheduler) {
        reShapeEvents(data.getDriverUserScheduler.drivers)
      }
      setIsLoading(false)
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      })
      setIsLoading(false)
    },
  })

  const [reallocateJob, { loading: reallocationLoading }] = useLazyQuery(REALLOCATE_INTERNAL_DRIVER_JOB, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data) {
        getSchedulerData({
          variables: {
            view: viewMode,
            currentDate: currentDate.toISODate(),
          },
        })
        snackbar({ message: "Job reallocated successfully", variant: SnackBarVariant.SUCCESS })
        setSelectedEvent(null)
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      })
      setSelectedEvent(null)
    },
  })

  useEffect(() => {
    setIsLoading(true)
    getSchedulerData()
  }, [getSchedulerData])

  useEffect(() => {
    setIsLoading(true)
    getSchedulerData({
      variables: {
        view: viewMode,
        currentDate: currentDate.toISODate(),
      },
    })
  }, [viewMode, currentDate, getSchedulerData])

  const buildEvents = (drivers: any[]) => {
    const events: any = [];
    drivers.forEach((driverObj: any) => {
      const user = driverObj.user;
      const driverId = user.id;

      driverObj.dailySchedule.forEach((daily: any) => {
        const { jobs } = daily;
        jobs.forEach((job: any) => {
          const startTime = d.fromISO(job.jobDate)
          const endTime = startTime.plus({ hours: 1 })
          events.push({
            resourceId: driverId,
            start: startTime.toISO(),
            end: endTime.toISO(),
            title: `${startTime.toFormat("hh:mm a")}`,
            className: "job-event",
            extendedProps: {
              jobId: job.id,
              driverName: `${user.firstName} ${user.lastName}`,
              jobDetails: job,
            },
          })
        })
      })
    })
    return events
  }

  const buildResources = (drivers: any[]) => {
    return drivers.map((driverObj) => {
      const user = driverObj.user
      return {
        id: user.id,
        title: user.firstName ? `${user.firstName} ${user.lastName}` : user.email,
        extendedProps: {
          partnerProfile: user.partnerProfile || {},
          email: user.email,
        },
      }
    })
  }

  const buildTimeOffEvents = (drivers: any[]) => {
    const timeOffEvents: any = []

    drivers.forEach((driverObj: any) => {
      const user = driverObj.user
      const driverId = user.id

      driverObj.dailySchedule.forEach((daily: any) => {
        const { events } = daily
        events.forEach((timeOff: any) => {
          const startTime = d.fromISO(timeOff.startDate)
          const endTime = d.fromISO(timeOff.endDate)

          timeOffEvents.push({
            resourceId: driverId,
            start: startTime.toISO(),
            end: endTime.toISO(),
            title: `${startTime.toFormat("hh:mm a")}`,
            color: timeOff.color || "#e74c3c",
            className: "time-off-event",
            extendedProps: {
              driverName: `${user.firstName} ${user.lastName}`,
              timeOffDetails: timeOff,
            },
          })
        })
      })
    })

    return timeOffEvents
  }

  const reShapeEvents = (driverScheduleData: any[]) => {
    const filteredDrivers = driverScheduleData.filter((driverObj) => {
      const user = driverObj?.user

      if (!user) return false

      const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase()
      const searchStr = searchQuery?.toLowerCase() ?? ""

      return fullName.includes(searchStr) || user.email?.toLowerCase()?.includes(searchStr)
    })

    const jobEvents = buildEvents(filteredDrivers)
    const timeOffEvents = buildTimeOffEvents(filteredDrivers)

    setDriverEvents([...jobEvents, ...timeOffEvents])
    setResources(buildResources(filteredDrivers))
    setActiveResources(buildResources(filteredDrivers))
  }

  useEffect(() => {
    if (fullCalendarRef.current) {
      const calendarApi = fullCalendarRef.current.getApi()
      calendarApi.changeView(getCalendarViewName())
      calendarApi.gotoDate(currentDate.toJSDate())
    }
  }, [viewMode, currentDate, fullCalendarRef.current])

  const getCalendarViewName = (): string => {
    switch (viewMode) {
      case ViewMode.DAY:
        return "resourceTimelineDay"
      case ViewMode.WEEK:
        return "resourceTimelineWeek"
      case ViewMode.MONTH:
      default:
        return "resourceTimelineMonth"
    }
  }

  const handlePreviousDate = () => {
    if (viewMode === ViewMode.DAY) {
      setCurrentDate((prev) => prev.minus({ days: 1 }))
    } else if (viewMode === ViewMode.WEEK) {
      setCurrentDate((prev) => prev.minus({ weeks: 1 }))
    } else {
      setCurrentDate((prev) => prev.minus({ months: 1 }))
    }
  }

  const handleNextDate = () => {
    if (viewMode === ViewMode.DAY) {
      setCurrentDate((prev) => prev.plus({ days: 1 }))
    } else if (viewMode === ViewMode.WEEK) {
      setCurrentDate((prev) => prev.plus({ weeks: 1 }))
    } else {
      setCurrentDate((prev) => prev.plus({ months: 1 }))
    }
  }

  const handleTodayView = () => {
    setCurrentDate(d.now().toUTC())
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchQuery(value)

    if (value.trim() === "") {
      setActiveResources(resources)
    } else {
      const filteredResources = resources.filter((resource) =>
        resource.title.toLowerCase().includes(value.toLowerCase()),
      )
      setActiveResources(filteredResources)
    }
  }

  const getViewIcon = () => {
    switch (viewMode) {
      case ViewMode.DAY:
        return <ViewDayIcon />
      case ViewMode.WEEK:
        return <DateRangeIcon />
      case ViewMode.MONTH:
        return <CalendarMonthIcon />
      default:
        return <DateRangeIcon />
    }
  }

  return (
    <Grid container spacing={1}>
      <Grid container item className="scheduler-heading" xs={6}>
        <Typography variant={"h2"} style={{ fontSize: "1.5em" }} color={"primary"}>{"Driver Scheduler"}</Typography>
      </Grid>

      <Grid item xs={12}>
        <Card elevation={0} className="scheduler-card">
          <CardContent>
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={2}
              mb={3}
            >
              {/* Search Field */}
              <TextField
                placeholder="Search drivers..."
                variant="outlined"
                value={searchQuery}
                onChange={handleSearch}
                className={classes.searchField}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                sx={{
                  width: { xs: "100%", md: 300 },
                  transition: "all 0.3s ease",
                }}
              />

              {/* Date Navigation */}
              <Box className={classes.dateNavigation}>
                <Button
                  variant="outlined"
                  onClick={handlePreviousDate}
                  startIcon={<ChevronLeftIcon />}
                  className={classes.navButton}
                  color="primary"
                >
                  {viewMode === ViewMode.DAY ? "Prev Day" : viewMode === ViewMode.WEEK ? "Prev Week" : "Prev Month"}
                </Button>

                <Typography variant="h6" className={classes.currentDateDisplay}>
                  {viewMode === ViewMode.DAY && currentDate.toFormat("MMMM d, yyyy")}
                  {viewMode === ViewMode.WEEK && `Week of ${currentDate.startOf("week").toFormat("MMM d")}`}
                  {viewMode === ViewMode.MONTH && currentDate.toFormat("MMMM yyyy")}
                </Typography>

                <Button
                  variant="outlined"
                  onClick={handleNextDate}
                  endIcon={<ChevronRightIcon />}
                  className={classes.navButton}
                  color="primary"
                >
                  {viewMode === ViewMode.DAY ? "Next Day" : viewMode === ViewMode.WEEK ? "Next Week" : "Next Month"}
                </Button>

                <Tooltip title="Go to today">
                  <Button
                    variant="contained"
                    onClick={handleTodayView}
                    startIcon={<TodayIcon />}
                    className={classes.navButton}
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    Today
                  </Button>
                </Tooltip>
              </Box>

              {/* View Mode Toggle */}
              <ToggleButtonGroup
                color="primary"
                exclusive
                value={viewMode}
                onChange={(_, newVal) => {
                  if (newVal) {
                    setViewMode(newVal)
                  }
                }}
                aria-label="View mode"
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  padding: 0.5,
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 1.5,
                    mx: 0.5,
                    px: 2,
                    py: 1,
                    fontWeight: 500,
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value={ViewMode.DAY}>
                  <ViewDayIcon sx={{ mr: 1 }} />
                  Day
                </ToggleButton>
                <ToggleButton value={ViewMode.WEEK}>
                  <DateRangeIcon sx={{ mr: 1 }} />
                  Week
                </ToggleButton>
                <ToggleButton value={ViewMode.MONTH}>
                  <CalendarMonthIcon sx={{ mr: 1 }} />
                  Month
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Legend */}
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              gap={2}
              mb={3}
              p={2}
              borderRadius={2}
              bgcolor={(theme) => alpha(theme.palette.background.paper, 0.7)}
            >

              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendDot}
                  sx={{ bgcolor: "#3498db", boxShadow: "0 0 0 2px rgba(52, 152, 219, 0.3)" }}
                />
                <Typography variant="body2">Jobs</Typography>
              </Box>

              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendDot}
                  sx={{ bgcolor: "#e74c3c", boxShadow: "0 0 0 2px rgba(231, 76, 60, 0.3)" }}
                />
                <Typography variant="body2">Events</Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ ml: "auto", fontStyle: "italic" }}>
                * Event start times are displayed on event cards
              </Typography>
            </Box>

            {/* Calendar Container */}
            <Box className={classes.calendarContainer}>
              <InfiniteScroll
                dataLength={activeResources.length}
                next={() => { }}
                hasMore={false}
                loader={
                  <Box p={2} textAlign="center">
                    <CircularProgress size={24} />
                  </Box>
                }
                scrollableTarget="scheduler-scrollable"
              >
                <FullCalendar
                  timeZone={userReducer.currentBranch.timeZone}
                  plugins={[resourceTimelinePlugin, interactionPlugin]}
                  schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_LICENCE_KEY}
                  initialView={getCalendarViewName()}
                  initialDate={currentDate.toISO()}
                  ref={fullCalendarRef}
                  droppable={true}
                  events={driverEvents}
                  eventContent={(eventInfo) => {
                    return <b>{eventInfo.event.title}</b>
                  }}
                  headerToolbar={false}
                  editable={false}
                  resourceAreaWidth={"20%"}
                  height="auto"
                  contentHeight="auto"
                  resources={activeResources}
                  resourceOrder="title"
                  resourceAreaColumns={[
                    {
                      field: "title",
                      headerContent: "Driver Name",
                    },
                  ]}
                  eventClick={(info) => setSelectedEvent(info.event)}
                  slotDuration="00:30:00"
                  slotLabelInterval="01:00"
                  slotLabelFormat={[
                    {
                      hour: 'numeric',
                      minute: '2-digit',
                      omitZeroMinute: false,
                      meridiem: 'short'
                    }
                  ]}
                  views={{
                    resourceTimelineDay: {
                      slotDuration: "00:30:00",
                      slotLabelInterval: "01:00",
                      slotLabelFormat: [
                        { hour: "numeric", minute: "2-digit", omitZeroMinute: false, meridiem: "short" }
                      ],
                      scrollTime: d.now().toFormat("HH:mm")
                    },
                    resourceTimelineWeek: {
                      slotDuration: "01:00:00",
                      slotLabelInterval: "01:00",
                      slotLabelFormat: [
                        { weekday: "short", day: "numeric", month: "numeric" },
                        { hour: "numeric", minute: "2-digit", omitZeroMinute: false, meridiem: "short" },
                      ],
                    },
                    resourceTimelineMonth: {
                      slotDuration: { days: 1 },
                      slotLabelInterval: { days: 1 },
                      slotLabelFormat: [{ weekday: "short", day: "numeric" }],
                      slotMinWidth: 75,
                      eventTimeFormat: { hour: "numeric", minute: "2-digit", meridiem: false },
                      displayEventEnd: false,
                    },
                  }}
                />
              </InfiniteScroll>
            </Box>

            {/* Event Details Dialog */}
            <EventDetailsDialog
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
              onReallocate={() => reallocateJob({ variables: { jobId: selectedEvent?.extendedProps?.jobId } })}
              loading={reallocationLoading}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(3px)",
        }}
        open={schedulerLoading || isLoading}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="primary" />
          <Typography variant="body2" fontWeight="medium">
            Loading scheduler data...
          </Typography>
        </Box>
      </Backdrop>
    </Grid>
  )
}

